import { Phone, Star, TrendingUp, UserRound } from 'lucide-react';

export function TopCustomersSection({ data }) {
  const safeData = Array.isArray(data) ? data : [];
  const sortedCustomers = [...safeData]
    .map((item) => ({
      name: item.name || item.CustomerName || 'Unknown Customer',
      phone: item.phone || item.PhoneNumber || '-',
      orders: item.orders || item.OrderCount || 0,
      amount: item.amount || item.NetSales || 0
    }))
    .sort((a, b) => b.amount - a.amount);

  const totalSales = sortedCustomers.reduce((sum, item) => sum + (item.amount || 0), 0);
  const customersWithPercentage = sortedCustomers.map((item) => ({
    ...item,
    percentage: totalSales > 0 ? ((item.amount || 0) / totalSales) * 100 : 0
  }));

  if (customersWithPercentage.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl">
              <UserRound className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">Top Customers</h3>
              <p className="text-sm text-slate-500">Highest spending customers</p>
            </div>
          </div>
        </div>
        <div className="text-center py-12">
          <UserRound className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No customer data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Top Customers</h3>
            <p className="text-sm text-slate-500">Highest spending customers</p>
          </div>
        </div>

        <div className="text-right">
          <p className="text-xs text-slate-500">Total Customers</p>
          <p className="text-lg font-bold text-slate-800">{customersWithPercentage.length}</p>
        </div>
      </div>

      <div className="space-y-3 max-h-[650px] overflow-y-auto pr-2 custom-scrollbar">
        {customersWithPercentage.slice(0, 10).map((customer, index) => (
          <div
            key={`${customer.name}-${customer.phone}-${index}`}
            className="group p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl hover:shadow-md transition-all border border-slate-200 hover:border-teal-200"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-800 truncate group-hover:text-[#01796d] transition-colors">
                      {customer.name}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {customer.phone}
                      </p>
                      {index === 0 && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Top Customer
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-[#01796d]">
                      Rs. {(customer.amount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Orders: {(customer.orders || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                    <span>Sales Share</span>
                    <span className="font-medium text-teal-600">
                      {(customer.percentage || 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-br from-cyan-500 to-teal-600 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(customer.percentage || 0, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {customersWithPercentage.length > 10 && (
        <div className="mt-4 pt-4 border-t border-slate-200 text-center">
          <p className="text-sm text-slate-500">
            Showing top 10 of {customersWithPercentage.length} customers
          </p>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}
